import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { memo } from 'react';
import type { ColumnDef, ColumnVisibilityOptions } from '~/components/list-table';
import { useColumnVisibility } from '~/components/list-table';
import type { CellContext } from '~/components/list-table/types';

interface SortableRowProps<T> {
  checked: boolean;
  columns: readonly ColumnDef<T>[];
  dragDisabled: boolean;
  focused: boolean;
  id: number;
  item: T;
  onRowFocus: (id: number) => void;
  onToggle: (id: number) => void;
  options?: ColumnVisibilityOptions;
}

function SortableRowInner<T>({
  checked,
  columns,
  dragDisabled,
  focused,
  id,
  item,
  onRowFocus,
  onToggle,
  options = {},
}: SortableRowProps<T>) {
  const theme = useTheme();
  const { visibleColumns, gridTemplateColumns } = useColumnVisibility(columns, options);

  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: dragDisabled });

  const activatorProps = dragDisabled ? {} : { ...attributes, ...listeners };

  const cellContext: CellContext = {
    checked,
    onToggle: () => onToggle(id),
    dragDisabled,
    isDragging,
    focused,
    setActivatorNodeRef,
    activatorProps,
  };

  return (
    <Box
      ref={setNodeRef}
      onClick={() => onRowFocus(id)}
      role="row"
      aria-selected={checked || undefined}
      tabIndex={-1}
      data-row-id={String(id)}
      data-checked={checked ? 'true' : undefined}
      data-focused={focused ? 'true' : undefined}
      sx={{
        display: 'grid',
        gridTemplateColumns,
        alignItems: 'center',
        px: 1,
        minHeight: 45,
        borderBottom: `1px solid ${theme.palette.table.borderColor}`,
        backgroundColor: focused
          ? theme.palette.table.focusedBackgroundColor
          : theme.palette.table.backgroundColor,
        cursor: isDragging ? 'grabbing' : 'pointer',
        transform: transform ? `${CSS.Transform.toString(transform)} translateZ(0)` : undefined,
        transition,
        ...(isDragging ? { willChange: 'transform' as const } : {}),
        opacity: isDragging ? 0 : 1,
        pointerEvents: isDragging ? 'none' : 'auto',
        '&:hover': {
          backgroundColor: theme.palette.table.hoverBackgroundColor,
        },
        '&[data-checked="true"]': {
          backgroundColor: theme.palette.table.backgroundColor,
          boxShadow: `inset 0 0 0 2px ${theme.palette.table.checkedBorderColor}, rgba(0, 0, 0, 0.1) 0px 1px 2px 0px`,
        },
        '&[data-focused="true"]': {
          backgroundColor: theme.palette.table.focusedBackgroundColor,
        },
      }}
    >
      {visibleColumns.map((col) => (
        <Box
          key={col.key}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: col.align,
            pl: 1,
            minWidth: 0,
            overflow: 'hidden',
            // The cell clips, which would cut an outset focus ring off its controls. Doubled to
            // outrank the controls' own focus styles, which are inserted after this.
            '&& :focus-visible, && .Mui-focusVisible': { outlineOffset: -2 },
          }}
          role="gridcell"
        >
          {col.renderCell?.(item, cellContext)}
        </Box>
      ))}
    </Box>
  );
}

export const SortableRow = memo(SortableRowInner) as typeof SortableRowInner;
